import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Check, Undo2, X } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import { invalidateOrderViews } from '@/components/order/lifecycle'
import type { AiProposal } from '@/types'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useToast } from '@/components/ui/toast'

const LABELS: Record<string, string> = {
  section: 'Section',
  sla_days: 'Standard SLA (days)',
  urgent_sla_days: 'Urgent SLA (days)',
  daily_capacity: 'Daily capacity (pcs)',
  order: 'Order',
  production_date: 'Production date',
  delivery_due: 'Delivery due',
}

function show(value: unknown, key: string): string {
  if (value === null || value === undefined) return key === 'daily_capacity' ? 'Unlimited' : '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/** Only the fields that change, so the card stays readable. */
function Diff({ before, after }: { before: AiProposal['before']; after: AiProposal['after'] }) {
  const keys = Object.keys(after ?? {}).filter((key) => (before ?? {})[key] !== (after ?? {})[key])
  const same = Object.keys(after ?? {}).filter((key) => !keys.includes(key) && (key === 'section' || key === 'order'))

  return (
    <dl className="mt-2 space-y-1.5 text-xs">
      {same.map((key) => (
        <div key={key} className="flex flex-wrap items-center gap-2">
          <dt className="text-foreground/60">{LABELS[key] ?? key}</dt>
          <dd className="font-medium text-foreground">{show(after?.[key], key)}</dd>
        </div>
      ))}
      {keys.map((key) => (
        <div key={key} className="flex flex-wrap items-center gap-2">
          <dt className="text-foreground/60">{LABELS[key] ?? key}</dt>
          <dd className="flex items-center gap-1.5">
            {before && key in before && (
              <>
                <span className="rounded bg-foreground/5 px-1.5 py-0.5 text-foreground/60" title="Now">
                  {show(before[key], key)}
                </span>
                <ArrowRight className="size-3 text-foreground/40" />
              </>
            )}
            <span className="rounded border border-primary/30 bg-primary/10 px-1.5 py-0.5 font-medium text-primary">
              {show(after?.[key], key)}
            </span>
          </dd>
        </div>
      ))}
    </dl>
  )
}

const STATUS_TEXT: Record<string, string> = {
  applied: 'Applied',
  undone: 'Undone',
  dismissed: 'Dismissed',
  expired: 'Expired - ask again so MPG AI can check the current numbers',
}

/**
 * A change MPG AI suggested. Nothing has changed until an admin presses Apply
 * here (client 2026-10-26: click to apply, never automatic). A change that
 * cannot be undone from the card, such as moving an order's date (the
 * customer is emailed), asks first in a popup.
 */
export default function ProposalCard({ proposal, onChanged }: { proposal: AiProposal; onChanged: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)

  const act = useMutation({
    mutationFn: (verb: 'apply' | 'undo' | 'dismiss') =>
      api.post<{ message: string }>(`/admin/ai/proposals/${proposal.id}/${verb}`).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(data.message ?? 'Done.')
      setConfirming(false)
      onChanged()
      // The change touched real settings or an order, so cached screens are stale.
      queryClient.invalidateQueries({ queryKey: ['admin-departments'] })
      invalidateOrderViews(queryClient)
    },
    onError: (error) => {
      toast.error('That did not work', errorMessage(error))
      setConfirming(false)
      onChanged()
    },
  })

  if (proposal.status !== 'pending') {
    const applied = proposal.status === 'applied'

    return (
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground/60">
        {applied ? <Check className="size-3.5 shrink-0 text-success" /> : <X className="size-3.5 shrink-0" />}
        <span className={applied ? 'text-foreground' : undefined}>{proposal.summary}</span>
        <span aria-hidden>&middot;</span>
        <span>
          {STATUS_TEXT[proposal.status] ?? proposal.status}
          {proposal.applied_by_name && proposal.status !== 'dismissed' && ` by ${proposal.applied_by_name}`}
          {proposal.applied_at && `, ${formatDateTime(proposal.applied_at)}`}
        </span>
        {applied && proposal.link && (
          <>
            <span aria-hidden>&middot;</span>
            <Link to={proposal.link.path} className="font-medium text-primary hover:underline">
              {proposal.link.label}
            </Link>
          </>
        )}
        {applied && proposal.supports_undo && (
          <>
            <span aria-hidden>&middot;</span>
            <button
              type="button"
              disabled={act.isPending}
              onClick={() => act.mutate('undo')}
              className="-m-1 inline-flex items-center gap-1 p-1 font-medium hover:text-foreground disabled:opacity-60"
            >
              <Undo2 className="size-3" /> Undo
            </button>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
      <p className="text-sm font-semibold text-foreground">{proposal.summary}</p>
      {proposal.rationale && <p className="mt-0.5 text-xs text-foreground/70">{proposal.rationale}</p>}

      <Diff before={proposal.before} after={proposal.after} />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          className="h-9 px-3"
          loading={act.isPending && act.variables === 'apply'}
          disabled={act.isPending}
          onClick={() => (proposal.supports_undo ? act.mutate('apply') : setConfirming(true))}
        >
          <Check className="size-4" /> Apply
        </Button>
        <Button
          variant="outline"
          className="h-9 px-3"
          disabled={act.isPending}
          onClick={() => act.mutate('dismiss')}
        >
          <X className="size-4" /> Dismiss
        </Button>
        <span className="text-xs text-foreground/50">Nothing changes until you press Apply.</span>
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => act.mutate('apply')}
        title="Apply this change?"
        confirmLabel="Apply"
        loading={act.isPending}
      >
        <p>{proposal.summary}.</p>
        <p className="mt-2">This cannot be undone from the chat. The order is booked again and the customer is emailed the new dates.</p>
      </ConfirmDialog>
    </div>
  )
}
